import { RIDE_STATUSES, DERIVED_RIDE_STATUSES, isClassPlaced } from "score-common";

// #1042: a class isn't publishable until it's actually done. Moved out of
// PublishResults.jsx by #2846 so the reason detection is unit-testable; the
// rules below are #1042/#3131's, unchanged, and #2846 only SPLITS the old
// reasons so the fold-out can say what is actually wrong.
//
// Checked in this order because an open ride also can't have a placing yet,
// so surfacing "N rides not yet closed" is the more actionable of the
// possible reasons:
//   0. no rides at all (or rides not loaded yet): no verdict, null. A 0-ride
//      class is a USEF Did-Not-Fill class that bulk Publish uploads on
//      purpose (report-api usef-serializer), so flagging it Incomplete would
//      block bulk Publish and so Finalize (#2846 deliberately does NOT do
//      this).
//   1. every non-scratched ride must be "closed": CONFIRMED or FINAL (#340's
//      classNeedsPlacings bar; DERIVED_RIDE_STATUSES.COMPLETED is still
//      partial, only some of the expected judges have signed, #791), a
//      SCRATCHED ride is resolved and never blocks (deriveRideStatus/
//      ride-status.js). #2846: when EVERY non-scratched ride of the
//      class (closed ones included) derives PENDING (no judge has marked
//      anything) the reason is "neverRan", not "openRides": the class reads as
//      though it never happened (R7/CDS 2026-09-20), which is a different thing
//      to chase than a class that is mid-run. A class with any scored ride
//      (e.g. FINAL + PENDING between rides) keeps "openRides" unchanged.
//   2. the class must be placed: isClassPlaced (score-common, #703): every
//      ride has a `placing`, is SCRATCHED, is terminated (#2335: #2069
//      forbids ever giving a terminated ride a placing, so it can only ever
//      resolve this way), or is a resolved emergency-manual placeholder
//      carrying resolvedIntoRideId (#2331). Same derivation the rider portal
//      gates on, so "placed" never has two different meanings in this repo.
//      #2846: when SOME rides carry a placing and others do not the reason is
//      "partlyPlaced" with the unplaced count, not "notPlaced". FVD places
//      ribbons only (top 5 or 6, see isClassPlacedForRiderSheets), so rides
//      beyond the ribbons have no placing until they are given a zero
//      (R7/CDS class 153: "class had placings but was missing zeros for the
//      rides beyond 6th. Added them, class is placed."). A class with no
//      placing at all stays "notPlaced" (still awaiting placings).
//
// #3131: isClassPlaced gets the sheets fetched for no-score-flagged rides
// (noScoreSheetsByRideId) so #1930's lapse rule applies here exactly as it
// does in setClassPlacings: a ride with an active ride.noScoreRecorded marker
// whose sheets hold real judged marks is NO LONGER resolved, so it blocks the
// class instead of riding its stale marker into a USEF publish. While the
// flagged rides' sheets are still in flight the map is null: a ride whose
// resolution would depend on the marker then counts as unresolved:
// fail-safe in the withholding direction (never releases a class early).
//
// #3174 (Jaybill picked option C on 2026-10-04): a class whose number is shared
// by another visible class row (classNumberTwins.js, #2843: FVD re-keyed the
// class and the scores are stranded on the stale row) is BLOCKED, not merely
// warned about. The optional 4th argument is that row's entry from
// findClassNumberTwins (undefined/null when the class has no twin). It is
// checked FIRST and regardless of ride count or status: both rows of the pair
// are held back from single and bulk Publish and from Finalize, and neither a
// closed-and-placed row nor a 0-ride Did-Not-Fill row is exempt while it is
// part of a twin pair (a 0-ride class with NO twin stays publishable, rule 0).
// Nothing is moved or deleted: the human re-points the scores (Score Admin,
// Re-point Scores, #837) and hides the stale row, which ends the twin pair.
//
// Returns null once all hold, else { reason, count }:
//   "duplicateClass" count = class rows sharing this class number,
//                    classNumber = the shared number
//   "neverRan"     count = rides that never started
//   "openRides"    count = rides not yet closed
//   "partlyPlaced" count = rides with no placing, placed = rides with one
//   "notPlaced"    count 0
export function classIncompleteInfo(rides, rideStatusByRideId, noScoreSheetsByRideId, twin) {
  if (twin) return { reason: "duplicateClass", count: (twin.others?.length ?? 0) + 1, classNumber: twin.classNumber };
  // Not loaded yet, or a 0-ride (Did-Not-Fill) class: no verdict.
  if (!rides || rides.length === 0) return null;
  const openRides = rides.filter((r) => {
    if (r.status === RIDE_STATUSES.SCRATCHED) return false;
    const derived = rideStatusByRideId?.[r.id];
    return derived !== DERIVED_RIDE_STATUSES.CONFIRMED && derived !== DERIVED_RIDE_STATUSES.FINAL;
  });
  if (openRides.length > 0) {
    const neverRan = rides
      .filter((r) => r.status !== RIDE_STATUSES.SCRATCHED)
      .every((r) => rideStatusByRideId?.[r.id] === DERIVED_RIDE_STATUSES.PENDING);
    return { reason: neverRan ? "neverRan" : "openRides", count: openRides.length };
  }
  // #3131: a ride whose isClassPlaced resolution would hinge on its
  // no-score-recorded marker (active, and not already resolved by placing/
  // scratch/placeholder: the same filters isClassPlaced applies before it
  // ever consults the marker) must not be judged off the marker alone while
  // its sheets are still loading (map === null).
  const markerDecides = rides.some(
    (r) =>
      r?.noScoreRecorded?.active === true &&
      r?.status !== RIDE_STATUSES.SCRATCHED &&
      r?.placing == null &&
      r?.resolvedIntoRideId == null,
  );
  if (markerDecides && noScoreSheetsByRideId == null) return { reason: "notPlaced", count: 0 };
  if (isClassPlaced(rides, noScoreSheetsByRideId)) return null;
  // Per-ride resolution through isClassPlaced itself (it is `every` over
  // rides, so a one-ride list is that ride's own answer): the unplaced count
  // can never disagree with the gate that blocked the class.
  const unplacedRides = rides.filter((r) => !isClassPlaced([r], noScoreSheetsByRideId));
  const placed = rides.filter((r) => r?.placing != null).length;
  // A ride held open by a lapsed no-score marker (#1930/#3131: its sheets
  // hold real marks, so it needs a real placing, not a 0) has a different fix
  // than "give the rest a zero", so it keeps the generic reason.
  const heldByNoScoreMarker = unplacedRides.some((r) => r?.noScoreRecorded?.active === true);
  if (placed > 0 && !heldByNoScoreMarker) return { reason: "partlyPlaced", count: unplacedRides.length, placed };
  return { reason: "notPlaced", count: 0 };
}
